import { useCallback, useState } from 'react';

import { DISCHARGE_REASON_OPTIONS } from '../constants';
import { DISCHARGE_REASON_DRAFT_SCHEMA } from '../validationSchema';
import { getDischargeReasonDraftError, toDischargeReasonPayload } from '../utils';

import type { IDischargeReason } from '../../../../../../store/treatmentPlanV3';
import type { SelectValueType } from '../../../../../../utils/types';
import type { UseDischargeReasonDraftParams } from './types';

export default function useDischargeReasonDraft({
  reasons,
  onCreate,
  onDelete,
}: UseDischargeReasonDraftParams) {
  const [reason, setReasonValue] = useState('');
  const [explanation, setExplanation] = useState('');
  const [pendingDeleteReason, setPendingDeleteReason] = useState<IDischargeReason | null>(null);
  const [showDraftError, setShowDraftError] = useState(false);

  const draft = { reason, explanation };
  const availableOptions = DISCHARGE_REASON_OPTIONS.filter(
    (option) => !reasons.some((added) => added.reason === String(option.value)),
  );

  const setReason = useCallback((value: SelectValueType) => {
    setReasonValue(String(value));
    setShowDraftError(false);
  }, []);

  const submit = useCallback(async () => {
    if (!DISCHARGE_REASON_DRAFT_SCHEMA.isValidSync({ reason, explanation })) {
      setShowDraftError(true);
      return;
    }
    if (await onCreate(toDischargeReasonPayload({ reason, explanation }))) {
      setReasonValue('');
      setExplanation('');
      setShowDraftError(false);
    }
  }, [explanation, onCreate, reason]);

  const requestDelete = useCallback((target: IDischargeReason) => {
    setPendingDeleteReason(target);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDeleteReason(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteReason === null) return;
    if (await onDelete(pendingDeleteReason.id)) {
      setPendingDeleteReason(null);
    }
  }, [onDelete, pendingDeleteReason]);

  return {
    draft,
    availableOptions,
    canAdd: reason !== '',
    draftError: showDraftError ? getDischargeReasonDraftError(draft) : undefined,
    pendingDeleteReason,
    setReason,
    setExplanation,
    submit,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
}
